export interface HypnogramSelection {
  time_seconds: number; start_seconds: number; end_seconds: number;
  stage: "Wake" | "N1" | "N2" | "N3" | "REM" | null;
  coverage: "scored" | "unknown" | "missing" | "unscored";
}
export interface HypnogramComponent {
  schema: "neoma.hypnogram/2"; type: "hypnogram";
  status: "available" | "unavailable"; reason: string | null;
  timeline: { unit: "seconds"; origin: "recording_start"; duration_seconds: number; start_seconds: number;
    epoch_seconds: number; encoding: "label_index_or_null"; labels: string[]; stages: (number | null)[]; unscored_tail_seconds: number } | null;
  interaction: "select_time"; fallback: string;
}
export interface HypnogramSelectionSource {
  metrics_run_ref: string; metrics_sha256: string; staging_run_ref: string; result_sha256: string; timeline_sha256: string;
}
function obj(v: unknown): Record<string, unknown> {
  if (!v || typeof v !== "object" || Array.isArray(v)) throw new TypeError("Invalid hypnogram object");
  return v as Record<string, unknown>;
}
function number(v: unknown): number {
  if (typeof v !== "number" || !Number.isFinite(v) || v < 0 || v > 604800) throw new TypeError("Invalid hypnogram time");
  return v;
}
function hash(v: unknown): boolean { return typeof v === "string" && /^[0-9a-f]{64}$/.test(v); }
function text(v: unknown): boolean { return typeof v === "string" && v.length > 0 && v.length <= 2000; }
export function parseHypnogramSelection(value: unknown): HypnogramSelection | null {
  if (value === null) return null;
  const s = obj(value);
  const time = number(s.time_seconds), start = number(s.start_seconds), end = number(s.end_seconds);
  if (start >= end || time < start || time > end || !["scored", "unknown", "missing", "unscored"].includes(String(s.coverage)) ||
    (s.coverage === "scored" ? !["Wake", "N1", "N2", "N3", "REM"].includes(String(s.stage)) : s.stage !== null)) throw new TypeError("Invalid selected interval");
  return s as unknown as HypnogramSelection;
}
export function parseHypnogramSource(value: unknown): HypnogramSelectionSource | null {
  if (value === null) return null;
  const s = obj(value);
  if (!text(s.metrics_run_ref) || typeof s.staging_run_ref !== "string" || !/^(?:run_[0-9a-f-]{36}|legacy_art_[0-9a-f]{64})$/.test(s.staging_run_ref) || !hash(s.metrics_sha256) || !hash(s.result_sha256) || !hash(s.timeline_sha256)) throw new TypeError("Invalid selection source");
  return s as unknown as HypnogramSelectionSource;
}
export function parseHypnogramComponent(value: unknown): HypnogramComponent {
  const p = obj(value);
  if (p.schema !== "neoma.hypnogram/2" || p.type !== "hypnogram" || p.interaction !== "select_time" || !text(p.fallback) ||
    Object.keys(p).some(key => !["schema", "type", "status", "reason", "timeline", "interaction", "fallback"].includes(key))) throw new TypeError("Invalid hypnogram payload");
  if (p.status === "available") {
    const t = obj(p.timeline);
    if (p.reason !== null || t.unit !== "seconds" || t.origin !== "recording_start" || t.encoding !== "label_index_or_null" ||
      Object.keys(t).some(key => !["unit", "origin", "duration_seconds", "start_seconds", "epoch_seconds", "encoding", "labels", "stages", "unscored_tail_seconds"].includes(key)) ||
      number(t.duration_seconds) <= 0 || number(t.epoch_seconds) <= 0 || JSON.stringify(t.labels) !== '["Wake","N1","N2","N3","REM"]' ||
      !Array.isArray(t.stages) || t.stages.length < 1 || t.stages.length > 8192 ||
      t.stages.some(s => s !== null && (typeof s !== "number" || !Number.isInteger(s) || s < 0 || s > 4)) ||
      number(t.start_seconds) + number(t.epoch_seconds) * t.stages.length + number(t.unscored_tail_seconds) > number(t.duration_seconds)) throw new TypeError("Invalid hypnogram timeline");
  } else if (p.status !== "unavailable" || p.timeline !== null || !text(p.reason)) throw new TypeError("Invalid unavailable hypnogram");
  return p as unknown as HypnogramComponent;
}
